import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
} from '@mui/material';
import { Delete } from '@mui/icons-material';
import { formatInTimeZone } from 'date-fns-tz';
import { apiService } from '../services/apiService';
import { studioIdOf } from '../utils/studioOf';

const IST_TIMEZONE = 'Asia/Kolkata';

export interface DeletableSession {
  _id: string;
  className: string;
  date: string;
  instructor?: { name?: string } | null;
}

interface DeleteSessionDialogProps {
  /** null = closed. */
  session: DeletableSession | null;
  onClose: () => void;
  onDeleted: () => void;
  onError: (message: string) => void;
}

/**
 * The "Delete Session" confirmation — one flow for the Sessions page list and
 * the schedule calendar (Sessions and Bookings pages).
 */
const DeleteSessionDialog: React.FC<DeleteSessionDialogProps> = ({ session, onClose, onDeleted, onError }) => {
  const [deleting, setDeleting] = useState(false);

  const handleDeleteConfirm = async () => {
    if (!session) return;
    try {
      setDeleting(true);
      // Pinned to the session's own location — under "All locations" the
      // switcher sends __all__, which the server refuses for writes.
      await apiService.deleteSession(session._id, studioIdOf((session as any).studio));
      onDeleted();
    } catch (err: any) {
      onError(err.message || 'Failed to delete session');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Dialog open={Boolean(session)} onClose={() => !deleting && onClose()} maxWidth="sm" fullWidth>
      <DialogTitle>Delete Session</DialogTitle>
      <DialogContent>
        <DialogContentText>
          Are you sure you want to delete <strong>"{session?.className}"</strong>?
        </DialogContentText>
        {session && (
          <Box mt={2}>
            <Typography variant="body2" color="text.secondary">
              <strong>Date & Time:</strong> {formatInTimeZone(new Date(session.date), IST_TIMEZONE, 'EEE, MMM d')} at{' '}
              {formatInTimeZone(new Date(session.date), IST_TIMEZONE, 'hh:mm a')}
              <br />
              <strong>Instructor:</strong> {session.instructor?.name || 'N/A'}
            </Typography>
          </Box>
        )}
        <Alert severity="warning" sx={{ mt: 2 }}>
          Sessions with existing bookings will <strong>NOT</strong> be deleted.
        </Alert>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={deleting}>Cancel</Button>
        <Button
          onClick={handleDeleteConfirm}
          color="error"
          variant="contained"
          disabled={deleting}
          startIcon={deleting ? <CircularProgress size={16} /> : <Delete />}
        >
          {deleting ? 'Deleting...' : 'Delete'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeleteSessionDialog;
